import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  FormLabel,
  Card,
  CardContent,
  Divider,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
  ListItemText,
  Chip
} from '@mui/material';
import {
  Person,
  School,
  ContactPhone,
  Upload,
  CheckCircle,
  ArrowForward,
  ArrowBack
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { motion } from 'framer-motion';
import { admissionAPI } from '../../services/api';
import { toast } from 'react-hot-toast';

const steps = [
  'Personal Information',
  'Academic Information',
  'Contact & Emergency',
  'Documents Upload',
  'Review & Submit'
];

const Admission = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const { control, handleSubmit, watch, formState: { errors }, trigger } = useForm({
    defaultValues: {
      // Personal Information
      firstName: '',
      lastName: '',
      middleName: '',
      dateOfBirth: '',
      gender: '',
      nationality: 'Nigerian',
      stateOfOrigin: '',
      localGovernment: '',
      religion: '',
      bloodGroup: '',
      
      // Academic Information
      previousSchool: '',
      lastClassCompleted: '',
      desiredClass: '',
      academicYear: '2024/2025',
      subjects: [],
      
      // Contact Information
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      postalCode: '',
      
      // Emergency Contact
      emergencyContactName: '',
      emergencyContactPhone: '',
      emergencyContactRelationship: '',
      emergencyContactAddress: '',
      
      // Parent/Guardian Information
      parentFirstName: '',
      parentLastName: '',
      parentPhone: '',
      parentEmail: '',
      parentOccupation: '',
      parentAddress: '',
      
      // Additional Information
      medicalConditions: '',
      allergies: '',
      medications: '',
      specialNeeds: '',
      transportation: '',
      previousDisciplinary: false,
      agreeToTerms: false,
    }
  });

  const handleNext = async () => {
    const fieldsToValidate = getFieldsForStep(activeStep);
    const isValid = await trigger(fieldsToValidate);
    
    if (isValid) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const getFieldsForStep = (step) => {
    switch (step) {
      case 0:
        return ['firstName', 'lastName', 'dateOfBirth', 'gender', 'nationality'];
      case 1:
        return ['previousSchool', 'lastClassCompleted', 'desiredClass'];
      case 2:
        return ['email', 'phone', 'address', 'emergencyContactName', 'emergencyContactPhone'];
      case 3:
        return [];
      case 4:
        return ['agreeToTerms'];
      default:
        return [];
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      const response = await admissionAPI.submitApplication(data);
      toast.success('Application submitted successfully!');
      console.log('Application submitted:', response.data);
      // Redirect to success page or show confirmation
    } catch (error) {
      toast.error('Failed to submit application. Please try again.');
      console.error('Error submitting application:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    try {
      const uploadPromises = files.map(file => {
        const formData = new FormData();
        formData.append('file', file);
        return admissionAPI.uploadDocuments(formData);
      });
      
      const responses = await Promise.all(uploadPromises);
      const newFiles = responses.map((res, index) => ({
        id: res.data.id,
        name: files[index].name,
        type: files[index].type,
        size: files[index].size,
        url: res.data.url
      }));
      
      setUploadedFiles(prev => [...prev, ...newFiles]);
      toast.success('Files uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload files. Please try again.');
      console.error('Error uploading files:', error);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="firstName"
                control={control}
                rules={{ required: 'First name is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="First Name"
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="lastName"
                control={control}
                rules={{ required: 'Last name is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Last Name"
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="middleName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Middle Name"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="dateOfBirth"
                control={control}
                rules={{ required: 'Date of birth is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="date"
                    label="Date of Birth"
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.dateOfBirth}
                    helperText={errors.dateOfBirth?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="gender"
                control={control}
                rules={{ required: 'Gender is required' }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.gender}>
                    <InputLabel>Gender</InputLabel>
                    <Select {...field} label="Gender">
                      <MenuItem value="male">Male</MenuItem>
                      <MenuItem value="female">Female</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="nationality"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Nationality"
                  />
                )}
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Controller
                name="previousSchool"
                control={control}
                rules={{ required: 'Previous school is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Previous School"
                    error={!!errors.previousSchool}
                    helperText={errors.previousSchool?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="lastClassCompleted"
                control={control}
                rules={{ required: 'Last class completed is required' }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.lastClassCompleted}>
                    <InputLabel>Last Class Completed</InputLabel>
                    <Select {...field} label="Last Class Completed">
                      <MenuItem value="JSS1">JSS1</MenuItem>
                      <MenuItem value="JSS2">JSS2</MenuItem>
                      <MenuItem value="JSS3">JSS3</MenuItem>
                      <MenuItem value="SSS1">SSS1</MenuItem>
                      <MenuItem value="SSS2">SSS2</MenuItem>
                      <MenuItem value="SSS3">SSS3</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="desiredClass"
                control={control}
                rules={{ required: 'Desired class is required' }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.desiredClass}>
                    <InputLabel>Desired Class</InputLabel>
                    <Select {...field} label="Desired Class">
                      <MenuItem value="JSS1">JSS1</MenuItem>
                      <MenuItem value="JSS2">JSS2</MenuItem>
                      <MenuItem value="JSS3">JSS3</MenuItem>
                      <MenuItem value="SSS1">SSS1</MenuItem>
                      <MenuItem value="SSS2">SSS2</MenuItem>
                      <MenuItem value="SSS3">SSS3</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="email"
                control={control}
                rules={{ 
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="email"
                    label="Email Address"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="phone"
                control={control}
                rules={{ required: 'Phone number is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Phone Number"
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="address"
                control={control}
                rules={{ required: 'Address is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={3}
                    label="Address"
                    error={!!errors.address}
                    helperText={errors.address?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Emergency Contact Information
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="emergencyContactName"
                control={control}
                rules={{ required: 'Emergency contact name is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Emergency Contact Name"
                    error={!!errors.emergencyContactName}
                    helperText={errors.emergencyContactName?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="emergencyContactPhone"
                control={control}
                rules={{ required: 'Emergency contact phone is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Emergency Contact Phone"
                    error={!!errors.emergencyContactPhone}
                    helperText={errors.emergencyContactPhone?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Required Documents
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Please upload the following documents:
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Birth Certificate" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Previous School Report Card" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Passport Photograph" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Medical Certificate" />
              </ListItem>
            </List>
            <Box mt={3}>
              <input
                accept="image/*,.pdf"
                style={{ display: 'none' }}
                id="file-upload"
                multiple
                type="file"
                onChange={handleFileUpload}
              />
              <label htmlFor="file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<Upload />}
                  fullWidth
                >
                  Upload Documents
                </Button>
              </label>
            </Box>
            {uploadedFiles.length > 0 && (
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Uploaded Files:
                </Typography>
                {uploadedFiles.map((file, index) => (
                  <Chip
                    key={index}
                    label={file.name}
                    onDelete={() => setUploadedFiles(prev => prev.filter((_, i) => i !== index))}
                    sx={{ mr: 1, mb: 1 }}
                  />
                ))}
              </Box>
            )}
          </Box>
        );

      case 4:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review Your Application
            </Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
              Please review all information before submitting. You can go back to make changes if needed.
            </Alert>
            <Controller
              name="agreeToTerms"
              control={control}
              rules={{ required: 'You must agree to the terms and conditions' }}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} />}
                  label="I agree to the terms and conditions and privacy policy"
                />
              )}
            />
            {errors.agreeToTerms && (
              <Typography color="error" variant="body2">
                {errors.agreeToTerms.message}
              </Typography>
            )}
          </Box>
        );

      default:
        return 'Unknown step';
    }
  };

  return (
    <Box sx={{ py: 8, bgcolor: 'grey.50', minHeight: '100vh' }}>
      <Container maxWidth="md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box textAlign="center" mb={6}>
            <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
              Student Admission Application
            </Typography>
            <Typography variant="h6" color="textSecondary">
              Join Bridge of Life Academy and start your educational journey
            </Typography>
          </Box>

          <Paper sx={{ p: 4 }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            <Box sx={{ mt: 4 }}>
              {renderStepContent(activeStep)}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                startIcon={<ArrowBack />}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={activeStep === steps.length - 1 ? handleSubmit(onSubmit) : handleNext}
                endIcon={activeStep === steps.length - 1 ? <CheckCircle /> : <ArrowForward />}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} />
                ) : activeStep === steps.length - 1 ? (
                  'Submit Application'
                ) : (
                  'Next'
                )}
              </Button>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Admission;